import {AppButton} from '@/components/Button';
import RemoteImage from '@/components/Image/RemoteImage';
import PageSection from '@/components/Layout/PageSection';
import appConfig from '@/config/appConfig.typed';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import useIsBannerEnabled from '@/hooks/useIsBannerEnabled';
import usePageSectionTitle from '@/hooks/usePageSectionTitle';
import {getAriaLabel} from '@/services/accessibilityService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import {filterSectionsById} from '@/services/pageSectionService';
import arrayUtils from '@/utils/arrayUtils';
import {formatStringWithComponents} from '@/utils/stringUtils';

// hides the image edge behind the desktop text column
const DESKTOP_GRADIENT = 'linear-gradient(90deg, #121212 0%, #121212 50%, rgba(18, 18, 18, 0) 75%)';

const ClubPlusSection = ({id, className}) => {
    const isBannerEnabled = useIsBannerEnabled();
    const titleText = usePageSectionTitle(id);
    const banners = arrayUtils.sortArrayWithOrderField(filterSectionsById({list: appConfig.getClubPlusSection(), id}));
    const banner = banners.find(isBannerEnabled);

    if (!banner) return null;

    return (
        <PageSection id={id} className={className}>
            <PageSection.Title className='mb-4' text={titleText} />
            <ClubPlusBanner banner={banner} />
        </PageSection>
    );
};

const ClubPlusBanner = ({banner}) => {
    const {
        action,
        actionAriaLabelKey,
        buttonActionKey,
        highlightedTitleKey,
        imageMedia,
        rewardDescriptionKey,
        rewardImageMedia,
        rewardTitleKey,
        titleKey,
    } = banner;
    const localizedStrings = getLocalizedStrings();
    const {redirectToExternalLink} = useConfigExternalLink(action);
    const titleText = localizedStrings[titleKey];
    const buttonText = localizedStrings[buttonActionKey];

    return (
        <article className='relative h-[540px] w-full overflow-hidden rounded-xl bg-primary text-start text-romance md:h-[403px]'>
            <div className='absolute left-0 top-0 h-full w-full md:left-1/2 md:h-auto md:w-1/2 md:-translate-y-[12%] md:aspect-[722/1082]'>
                <RemoteImage className='absolute left-0 top-0 h-full w-full object-cover' imageMedia={imageMedia} />
            </div>
            <div className='absolute hidden h-full w-full md:block' style={{background: DESKTOP_GRADIENT}} />
            <div className='relative z-[1] flex h-full flex-col justify-end gap-4 p-4 md:w-1/2 md:justify-center md:gap-6 md:p-8 lg:p-16'>
                <div className='flex flex-col gap-4 md:gap-3.5'>
                    {titleText && (
                        <h3 className='mb-0 text-42 font-bold leading-[44px]'>
                            {formatStringWithComponents(
                                titleText,
                                <span className='text-tertiary'>{localizedStrings[highlightedTitleKey]}</span>
                            )}
                        </h3>
                    )}
                    <ClubPlusReward
                        imageMedia={rewardImageMedia}
                        titleText={localizedStrings[rewardTitleKey]}
                        descriptionText={localizedStrings[rewardDescriptionKey]}
                    />
                </div>
                {buttonText && (
                    <AppButton
                        primary
                        dark
                        className='mt-0 ml:w-full'
                        onClick={redirectToExternalLink}
                        aria-label={getAriaLabel(actionAriaLabelKey)}
                    >
                        {buttonText}
                    </AppButton>
                )}
            </div>
        </article>
    );
};

const ClubPlusReward = ({imageMedia, titleText, descriptionText}) => {
    if (!titleText && !descriptionText) return null;

    return (
        <div className='flex h-[104px] overflow-hidden rounded-xl bg-mine-shaft'>
            <div className='relative w-[88px] shrink-0 opacity-30'>
                <RemoteImage className='absolute h-full w-full object-cover' imageMedia={imageMedia} />
            </div>
            <div className='flex flex-col justify-center gap-1 px-4'>
                {titleText && <p className='mb-0 text-16 leading-15 text-tertiary'>{titleText}</p>}
                {descriptionText && <p className='mb-0 text-16 leading-15'>{descriptionText}</p>}
            </div>
        </div>
    );
};

export default ClubPlusSection;
